import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Slider } from '../../components/ui/slider';
import { Checkbox } from '../../components/ui/checkbox';
import {
  ArrowLeft, Search, Users, Star, MapPin, Instagram, 
  Loader2, Filter, CheckCircle2, ExternalLink, Grid, 
  Award, TrendingUp, Shield, Clock, User, RefreshCw, X, Heart
} from 'lucide-react';
import { toast } from 'sonner';

// Social platform icons (simplified - using text for platforms without icons)
const PlatformIcon = ({ platform }) => {
  switch (platform) {
    case 'instagram':
      return <Instagram className="w-4 h-4 text-pink-500" />;
    case 'tiktok':
      return <span className="text-xs font-bold">TikTok</span>;
    case 'twitter':
      return <span className="text-xs font-bold text-blue-400">X</span>;
    case 'youtube':
      return <span className="text-xs font-bold text-red-500">YT</span>;
    case 'linkedin':
      return <span className="text-xs font-bold text-blue-600">in</span>;
    default:
      return null;
  }
};

// Tier badge colors
const getTierBadge = (tier) => {
  switch (tier) {
    case 'nano':
      return <Badge className="bg-cyan-100 text-cyan-700 border-cyan-200">Nano (5K-10K)</Badge>;
    case 'micro':
      return <Badge className="bg-blue-100 text-blue-700 border-blue-200">Micro (10K-50K)</Badge>;
    case 'mid':
      return <Badge className="bg-purple-100 text-purple-700 border-purple-200">Mid-Tier (50K-500K)</Badge>;
    case 'macro':
      return <Badge className="bg-amber-100 text-amber-700 border-amber-200">Macro (500K+)</Badge>;
    default:
      return null;
  }
};

// Format follower count
const formatCount = (num) => {
  if (!num) return '0';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

const InfluencerDiscovery = () => {
  const navigate = useNavigate();
  const [influencers, setInfluencers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [selectedInfluencer, setSelectedInfluencer] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    platform: '',
    tier: '',
    minFollowers: 0,
    verifiedOnly: false,
    city: '',
    state: '',
    roleId: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [favIds, setFavIds] = useState(new Set());

  useEffect(() => {
    fetchRoles();
    fetchInfluencers();
    fetchFavIds();
  }, []);

  const fetchFavIds = async () => {
    try {
      const res = await api.get('/client/favorites');
      const ids = (res.data || []).map(f => f.talent_id);
      setFavIds(new Set(ids));
    } catch {}
  };

  const toggleFav = async (talentId) => {
    try {
      if (favIds.has(talentId)) {
        await api.delete(`/client/favorites/${talentId}`);
        setFavIds(prev => { const n = new Set(prev); n.delete(talentId); return n; });
        toast.success('Removed from favorites');
      } else {
        await api.post('/client/favorites', { talent_id: talentId });
        setFavIds(prev => new Set([...prev, talentId]));
        toast.success('Added to favorites');
      }
    } catch { toast.error('Failed to update favorite'); }
  };

  const fetchRoles = async () => {
    try {
      const res = await api.get('/roles');
      setRoles(res.data || []);
    } catch (e) {
      console.error('Failed to fetch roles:', e);
    }
  };

  const fetchInfluencers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.platform) params.append('platform', filters.platform);
      if (filters.tier) params.append('tier', filters.tier);
      if (filters.minFollowers > 0) params.append('min_followers', filters.minFollowers);
      if (filters.verifiedOnly) params.append('verified_only', 'true');
      if (filters.city) params.append('city', filters.city);
      if (filters.state) params.append('state', filters.state);
      if (filters.roleId) params.append('role_id', filters.roleId);
      params.append('limit', '50');
      
      const res = await api.get(`/influencer/discover?${params.toString()}`);
      setInfluencers(res.data || []);
    } catch (e) {
      toast.error('Failed to load influencers');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    fetchInfluencers();
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({
      platform: '',
      tier: '',
      minFollowers: 0,
      verifiedOnly: false,
      city: '',
      state: '',
      roleId: ''
    });
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="influencer-discovery">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/client/dashboard')}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <Users className="w-5 h-5 text-[#03DFFC]" />
            <h1 className="text-lg font-semibold text-gray-900">Influencer Discovery</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowFilters(!showFilters)}
              className={showFilters ? 'bg-cyan-50 border-cyan-200' : ''}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </Button>
            <Button variant="ghost" size="sm" onClick={fetchInfluencers}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Filters Panel */}
        {showFilters && (
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {/* Platform */}
                <div>
                  <label className="text-sm text-gray-600 mb-1 block">Platform</label>
                  <Select value={filters.platform || 'all'} onValueChange={(v) => setFilters({...filters, platform: v === 'all' ? '' : v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Platforms" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Platforms</SelectItem>
                      <SelectItem value="instagram">Instagram</SelectItem>
                      <SelectItem value="tiktok">TikTok</SelectItem>
                      <SelectItem value="twitter">Twitter/X</SelectItem>
                      <SelectItem value="youtube">YouTube</SelectItem>
                      <SelectItem value="linkedin">LinkedIn</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Tier */}
                <div>
                  <label className="text-sm text-gray-600 mb-1 block">Influencer Tier</label>
                  <Select value={filters.tier || 'all'} onValueChange={(v) => setFilters({...filters, tier: v === 'all' ? '' : v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Tiers" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Tiers</SelectItem>
                      <SelectItem value="nano">Nano (5K-10K)</SelectItem>
                      <SelectItem value="micro">Micro (10K-50K)</SelectItem>
                      <SelectItem value="mid">Mid-Tier (50K-500K)</SelectItem>
                      <SelectItem value="macro">Macro (500K+)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Role */}
                <div>
                  <label className="text-sm text-gray-600 mb-1 block">Role Capability</label>
                  <Select value={filters.roleId || 'all'} onValueChange={(v) => setFilters({...filters, roleId: v === 'all' ? '' : v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Roles" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Roles</SelectItem>
                      {roles.map(role => (
                        <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Location */}
                <div>
                  <label className="text-sm text-gray-600 mb-1 block">City</label>
                  <Input 
                    placeholder="Any City"
                    value={filters.city}
                    onChange={(e) => setFilters({...filters, city: e.target.value})}
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-600 mb-1 block">State</label>
                  <Input 
                    placeholder="Any State"
                    value={filters.state}
                    onChange={(e) => setFilters({...filters, state: e.target.value})}
                  />
                </div>

                {/* Verified Only */}
                <div className="flex flex-col justify-end">
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="verified-only"
                      checked={filters.verifiedOnly}
                      onCheckedChange={(v) => setFilters({...filters, verifiedOnly: v})}
                    />
                    <label htmlFor="verified-only" className="text-sm cursor-pointer">
                      Verified Only
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  Clear Filters
                </Button>
                <Button onClick={applyFilters} className="bg-[#03DFFC] hover:bg-[#03DFFC]">
                  Apply Filters
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Results Count */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-gray-600">
            Found <strong>{influencers.length}</strong> influencer{influencers.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Influencer Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#03DFFC]" />
          </div>
        ) : influencers.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-gray-500 mx-auto mb-3" />
            <p className="text-gray-500">No influencers found matching your criteria</p>
            <p className="text-sm text-gray-500 mt-1">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {influencers.map((influencer) => (
              <Card 
                key={influencer.user_id} 
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => setSelectedInfluencer(influencer)}
                data-testid={`influencer-card-${influencer.user_id}`}
              >
                <CardContent className="p-4">
                  {/* Favorite Button */}
                  <div className="flex justify-end mb-1">
                    <button
                      onClick={e => { e.stopPropagation(); toggleFav(influencer.user_id); }}
                      className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${favIds.has(influencer.user_id) ? 'bg-[#FF2D95] text-white' : 'bg-gray-100 text-gray-400 hover:text-[#FF2D95]'}`}
                      data-testid={`fav-influencer-${influencer.user_id}`}
                    >
                      <Heart className={`w-4 h-4 ${favIds.has(influencer.user_id) ? 'fill-white' : ''}`} />
                    </button>
                  </div>
                  {/* Header */}
                  <div className="flex items-start gap-3 mb-3">
                    {influencer.photo ? (
                      <img 
                        src={influencer.photo} 
                        alt={influencer.name}
                        className="w-14 h-14 rounded-full object-cover border-2 border-gray-200"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center">
                        <User className="w-6 h-6 text-gray-500" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900 truncate">{influencer.name}</h3>
                        {influencer.is_verified && (
                          <CheckCircle2 className="w-4 h-4 text-[#03DFFC] flex-shrink-0" data-testid="verified-badge" />
                        )}
                      </div>
                      <p className="text-sm text-gray-500">{influencer.city}, {influencer.state}</p>
                      {influencer.primary_role && (
                        <p className="text-xs text-cyan-600 font-medium">{influencer.primary_role}</p>
                      )}
                    </div>
                  </div>

                  {/* Tier & Verification Status */}
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    {getTierBadge(influencer.influencer_tier)}
                    {influencer.verification_status === 'pending_verification' && (
                      <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">
                        <Clock className="w-3 h-3 mr-1" />
                        Pending Verification
                      </Badge>
                    )}
                    {influencer.is_verified && (
                      <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">
                        <Shield className="w-3 h-3 mr-1" />
                        Verified
                      </Badge>
                    )}
                  </div>

                  {/* Social Stats */}
                  <div className="space-y-1.5 mb-3">
                    {influencer.social_stats.instagram.followers > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-1.5 text-gray-600">
                          <Instagram className="w-4 h-4 text-pink-500" />
                          Instagram
                        </span>
                        <span className="font-medium">{formatCount(influencer.social_stats.instagram.followers)}</span>
                      </div>
                    )}
                    {influencer.social_stats.tiktok.followers > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-1.5 text-gray-600">
                          <span className="text-xs font-bold">TT</span>
                          TikTok
                        </span>
                        <span className="font-medium">{formatCount(influencer.social_stats.tiktok.followers)}</span>
                      </div>
                    )}
                    {influencer.social_stats.twitter.followers > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-1.5 text-gray-600">
                          <span className="text-xs font-bold text-blue-400">X</span>
                          Twitter/X
                        </span>
                        <span className="font-medium">{formatCount(influencer.social_stats.twitter.followers)}</span>
                      </div>
                    )}
                    {influencer.social_stats.youtube.subscribers > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-1.5 text-gray-600">
                          <span className="text-xs font-bold text-red-500">YT</span>
                          YouTube
                        </span>
                        <span className="font-medium">{formatCount(influencer.social_stats.youtube.subscribers)}</span>
                      </div>
                    )}
                  </div>

                  {/* Total Reach */}
                  <div className="pt-2 border-t border-gray-100">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500 flex items-center gap-1">
                        <TrendingUp className="w-4 h-4" />
                        Total Reach
                      </span>
                      <span className="font-bold text-cyan-600">{formatCount(influencer.total_reach)}</span>
                    </div>
                    {influencer.reliability_score && (
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs text-gray-500">Reliability</span>
                        <span className="text-xs font-medium text-green-600">{influencer.reliability_score.toFixed(1)}/5.0</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedInfluencer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-50/50 p-4">
          <Card className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardHeader className="border-b">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">{selectedInfluencer.name}</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setSelectedInfluencer(null)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              {/* Profile Header */}
              <div className="flex items-center gap-4">
                {selectedInfluencer.photo ? (
                  <img 
                    src={selectedInfluencer.photo} 
                    alt={selectedInfluencer.name}
                    className="w-20 h-20 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center">
                    <User className="w-10 h-10 text-gray-500" />
                  </div>
                )}
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-bold">{selectedInfluencer.name}</h2>
                    {selectedInfluencer.is_verified && (
                      <CheckCircle2 className="w-5 h-5 text-[#03DFFC]" />
                    )}
                  </div>
                  <p className="text-gray-500">{selectedInfluencer.city}, {selectedInfluencer.state}</p>
                  {selectedInfluencer.primary_role && (
                    <Badge variant="outline" className="mt-1">{selectedInfluencer.primary_role}</Badge>
                  )}
                </div>
              </div>

              {/* Badges */}
              <div className="flex flex-wrap gap-2">
                {getTierBadge(selectedInfluencer.influencer_tier)}
                {selectedInfluencer.is_verified && (
                  <Badge className="bg-green-100 text-green-700 border-green-200">
                    <Shield className="w-3 h-3 mr-1" />
                    Verified Influencer
                  </Badge>
                )}
              </div>

              {/* Bio */}
              {selectedInfluencer.bio && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-1">About</h4>
                  <p className="text-sm text-gray-600">{selectedInfluencer.bio}</p>
                </div>
              )}

              {/* Social Stats */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Social Media Reach</h4>
                <div className="space-y-2">
                  {Object.entries(selectedInfluencer.social_stats).map(([platform, stats]) => {
                    if (stats.followers > 0 || stats.subscribers > 0 || stats.connections > 0) {
                      const count = stats.followers || stats.subscribers || stats.connections;
                      const handle = stats.handle || stats.channel || stats.url;
                      return (
                        <div key={platform} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <span className="capitalize font-medium">{platform}</span>
                          <div className="text-right">
                            <span className="font-bold text-cyan-600">{formatCount(count)}</span>
                            {handle && (
                              <p className="text-xs text-gray-500">{handle}</p>
                            )}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
                <div className="mt-3 p-3 bg-cyan-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-cyan-800">Total Reach</span>
                    <span className="text-xl font-bold text-cyan-600">{formatCount(selectedInfluencer.total_reach)}</span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => toggleFav(selectedInfluencer.user_id)}
                  className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all border ${favIds.has(selectedInfluencer.user_id) ? 'bg-[#FF2D95] text-white border-[#FF2D95]' : 'bg-white text-gray-400 border-gray-200 hover:text-[#FF2D95] hover:border-[#FF2D95]/30'}`}
                  data-testid="detail-fav-btn"
                >
                  <Heart className={`w-5 h-5 ${favIds.has(selectedInfluencer.user_id) ? 'fill-white' : ''}`} />
                </button>
                <Button 
                  className="flex-1 bg-[#03DFFC] hover:bg-[#03DFFC]/90 text-white"
                  onClick={() => navigate(`/client/discover/${selectedInfluencer.user_id}`)}
                >
                  View Full Profile
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate(`/client/discover/${selectedInfluencer.user_id}?action=book`)}
                >
                  Book for Event
                </Button>
              </div>

              {/* Portfolio / Media Section */}
              {selectedInfluencer.portfolio_photos?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Portfolio & Media</h4>
                  <div className="grid grid-cols-3 gap-2">
                    {selectedInfluencer.portfolio_photos.slice(0, 6).map((url, i) => (
                      <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="block aspect-square rounded-lg overflow-hidden hover:opacity-80 transition-opacity">
                        <img src={url} alt={`Media ${i+1}`} className="w-full h-full object-cover" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Reels / Video Links */}
              {(selectedInfluencer.social_stats?.instagram?.handle || selectedInfluencer.social_stats?.tiktok?.handle) && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">View Content</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedInfluencer.social_stats?.instagram?.handle && (
                      <a href={`https://instagram.com/${selectedInfluencer.social_stats.instagram.handle.replace('@','')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs rounded-full hover:opacity-90 transition-opacity">
                        Reels & Posts
                      </a>
                    )}
                    {selectedInfluencer.social_stats?.tiktok?.handle && (
                      <a href={`https://tiktok.com/@${selectedInfluencer.social_stats.tiktok.handle.replace('@','')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-full hover:opacity-90 transition-opacity">
                        TikTok Videos
                      </a>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default InfluencerDiscovery;
